import axios from 'axios';
import { AnyAction, Dispatch } from 'redux';
import { setTaskLoader } from '../actions/taskActions';
import {
  convertBase64ToFile,
  fetchAndConvertToBase64,
  getAPIErrorReason,
  getFirebaseUrlFromFile,
  getHoursFromTime,
  isEmpty
} from '../helpers/common';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import {
  CommentFilesInterface,
  EmojiInterface,
  TaskGroupList,
  CreateTaskInputInterface,
  taskLinkInterface,
  TaskGroupInterface
} from '../interfaces/TaskInterface';
import { captureException } from './logService';
import { RootReducerInterface } from '../interfaces/RootReducerInterface';
import { ReplyMessageDataInterface } from '../interfaces/MessageInterface';
import { rootStore } from '../mobx/rootStore';
import { formatToRawComment, formatToRawTask } from '../mobx/common';
import { syncTasks } from './syncService';

/**
 * @desc Update task data
 * @param {*}
 */
export const updateTaskDetails: any = (taskId: string, payload: any) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    if (!taskId) return false;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/updateTask/${taskId}`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update task list please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

export const getRawTaskLinks = (item: any) => {
  return {
    createdBy: item?.CreatedBy,
    createdTime: item?.CreatedTime,
    iconUrl: item?.IconUrl,
    label: item?.Label,
    readonly: item?.Readonly,
    reference: item?.Reference,
    type: item?.Type,
    updatedBy: item?.UpdatedBy,
    updatedTime: item?.UpdatedTime,
    id: item?.['_id']
  };
};

export const updateCurrentTaskData = (data: any) => {
  const currentTask = rootStore.taskStore.currentTask;
  const commentData = data?.Comments?.map(formatToRawComment) || [];
  currentTask?.setComments(commentData);
  currentTask?.setFiles(data?.Files || []);
};

/**
 * @desc Get task details data
 * @param {*} taskId -The unique identifier of the task to fetch details.
 */
export const getMyTaskDetailsData: any =
  (taskId: string) => async (dispatch: Dispatch<AnyAction>, getState: () => RootReducerInterface) => {
    try {
      dispatch(setTaskLoader(true));

      const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getTaskDetails/${taskId}`);
      const { data } = response;

      if (!data) return false;

      //Update Mobx Task Details
      updateCurrentTaskData(data);
      syncTasks();

      return data;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get my task details please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc create new comment
 * @param {*} taskId -- The unique identifier of the task to fetch details.
 */
export const createNewComment: any =
  (
    taskId: string,
    payload: {
      Comment: string;
      Files: CommentFilesInterface[];
      Type: number;
      Reply?: ReplyMessageDataInterface;
    }
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/addComment/${taskId}`, payload);
      const { data } = response;
      if (data) {
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to create new comment please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc Update task comment
 * @param {*} taskId commentId payload
 */
export const updateTaskComment: any =
  (
    taskId: string,
    commentId: string,
    payload: {
      Comment?: string;
      IsArchived?: boolean;
      Files?: CommentFilesInterface[];
    }
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/comment/${commentId}`, payload);
      const { data } = response;
      if (data) {
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to update comment please try again';
      captureException(error);
      dispatchTaskError(getAPIErrorReason(e) || 'Unable to update comment please try again');
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc get timelog data
 * @param {*}
 */
export const getTimelogData: any = (projectId: string, taskId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const params = {
      projectId: projectId,
      taskId: taskId
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/getTimeLogByTask`, {
      params
    });
    const { data } = response;
    if (data) {
      const updatedData = data?.map((item: any) => {
        return { ...item, hours: item?.timerStartedAt ? getHoursFromTime(item?.timerStartedAt) : item?.hours };
      });

      // Update the MobX store
      if (rootStore.taskStore.currentTask) {
        rootStore.taskStore.currentTask.setTimeLogs(updatedData);
      }
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to get timelog data for task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 * @desc Project - get project task
 * @param {string} project_id - The unique identifier of the project to fetch task groups.
 * @param {boolean} isForceLoading - Flag to force loading of task groups.
 */
export const getTaskGroupList: any =
  (project_id: string, isSelect?: boolean, isForceLoading = true) =>
  async (dispatch: Dispatch<AnyAction>, getState: () => RootReducerInterface) => {
    try {
      dispatch(setTaskLoader(true));
      if (!isForceLoading) {
        const taskGroups = rootStore.projectStore.projects.find((project) => project.id === project_id)?.taskGroups;
        if (taskGroups && taskGroups?.length > 0) return taskGroups;
      }
      const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/getTaskGroupList/${project_id}`);
      const { data } = response;
      data.sort((a: { Index: number }, b: { Index: number }) => {
        return a.Index - b.Index;
      });
      if (data.length > 0) {
        const newData: TaskGroupInterface[] = data.map((element: TaskGroupList) => {
          return {
            id: element['_id'],
            index: element?.Index,
            name: element?.Name,
            projectId: element?.ProjectId,
            isCollapse: false
          };
        });

        //mobx set
        rootStore.projectStore.projects.find((project) => project.id === project_id)?.setTaskGroups(newData);
        return newData;
      }
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get task group list please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc Project - deleteTaskGroup
 * @param {string} taskGroupId - ID of the task group to delete
 */
export const deleteTaskGroup: any = (taskGroupId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const params = {
      id: taskGroupId
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/deleteTaskGroup`, { params });
    if (response) {
      dispatchTaskSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete task group please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 * @desc Project - tasks - create new task
 * @param {*}
 */
export const createNewTask: any = (payload: CreateTaskInputInterface) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/createTask`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 *  @desc Project - update group
 * @param {boolean} isIndexSetting - Optional flag to bypass validation
 */
export const updateGroupDetail: any =
  (payload: { name: string }, isIndexSetting = false) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      if (isEmpty(payload?.name) && !isIndexSetting) {
        dispatchTaskError('Please enter task group name');
        return;
      }
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/putTaskGroup`, payload);
      if (response) {
        if (!isIndexSetting) dispatchTaskSuccess(response.data.message);
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to update group please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 *  @desc Project - create new group
 * @param {*}
 */
export const createNewGroup: any = (payload: { name: string }) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    if (isEmpty(payload?.name)) {
      dispatchTaskError('Please enter task group name');
      return;
    }
    dispatch(setTaskLoader(true));
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/postTaskGroup`, payload);
    if (response) {
      dispatchTaskSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create group please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 *  @desc Get Figma preview URL
 * @param {*}
 */
export const getFigmaPreviewUrl: any =
  (payload: any) => async (dispatch: Dispatch<AnyAction>, getState: () => RootReducerInterface) => {
    let reference = 0;
    try {
      dispatch(setTaskLoader(true));
      let response: any;
      const state = getState();
      const { workspace } = state.workspace;
      const updatedPayload = { ...payload, companyId: workspace?.id };
      const fetchResultAfterDelay = async () => {
        try {
          reference = reference + 1;
          response = await axios.post(
            // eslint-disable-next-line max-len
            `${REACT_APP_TEAMCAMP_APIURL}/Tasks/getFigmaPreviewUrl`,
            updatedPayload
          );
        } catch (e) {
          if (reference <= 5) await fetchResultAfterDelay();
          else {
            dispatchTaskError('Unable to get figma preview. Refresh page and try again');
            return undefined;
          }
        }
      };
      await fetchResultAfterDelay();
      if (response?.data) {
        const base64Data = await fetchAndConvertToBase64(response?.data?.filePreviewUrl);
        const fileData = await convertBase64ToFile(base64Data);
        const fileUrl = await getFirebaseUrlFromFile({ file: fileData }, 'figma-preview/', dispatch);
        return { ...(response?.data || {}), filePreviewUrl: fileUrl };
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get figma preview';
      captureException(error);
      dispatchTaskError(error);
      return undefined;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 *  @desc task - comment - add reaction
 * @param {*}
 */
export const addReactionOnCommect: any = (payload: EmojiInterface) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/emojiReactions`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add reaction in comment please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 * @desc Get all recurring tasks
 * @param {*}
 */
export const getAllRecurringTasks: any =
  () => async (dispatch: Dispatch<AnyAction>, getState: () => RootReducerInterface) => {
    try {
      dispatch(setTaskLoader(true));
      const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getAllRecurringTasks`);
      const { data } = response;
      if (data) {
        const allTasks = data.flatMap((item: any) => item.tasks);
        const recurringTasks = allTasks.map(formatToRawTask);
        rootStore.taskStore.setRecurringTasks(recurringTasks);
        return recurringTasks;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get recurring tasks please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * Delete a recurring task by its ID and associated project ID.
 * @param {string} taskId - ID of the recurring task
 * @param {string} project_id - ID of the project
 */
export const deleteRecurringTask: any =
  (taskId: string, project_id: string) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      if (!taskId) return false;
      const response = await axios.delete(
        `${REACT_APP_TEAMCAMP_APIURL}/Tasks/removeRecurringTask/${taskId}?projectId=${project_id}`
      );

      if (response) {
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to delete recurring task  please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc Update recurring task data
 * @param {string} recurring_task_id - ID of the recurring task
 */
export const updateRecurringTaskDetails: any =
  (recurring_task_id: string, payload: any) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      if (!recurring_task_id) return false;
      const response = await axios.put(
        `${REACT_APP_TEAMCAMP_APIURL}/Tasks/updateRecurringTask/${recurring_task_id}`,
        payload
      );
      if (response) {
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to update recurring task please try again';
      captureException(error);
      dispatchTaskError(error);
      return false;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc get total number of task
 * @param {*}
 */
export const getTotalTaskCount: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getTotalTasks`);
    const { data } = response;
    if (data) {
      rootStore.workspaceStore.setWorkspaceTotalTask(data?.totalTasks);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

/**
 * @desc taskDetails - add resource
 * @param {string} taskId - ID of the task
 */
export const addTaskResource: any =
  (taskId: string, payload: taskLinkInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links`, payload);
      const { data } = response;
      if (data) {
        return data;
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'unable to add link please try again';
      captureException(error);
      dispatchTaskError(error);
      return undefined;
    } finally {
      dispatch(setTaskLoader(true));
    }
  };

/**
 * @desc task - update resource
 * @param {string} taskId - ID of the task
 * @param {string} linkId - ID of the resource link
 */
export const updateTaskResource: any =
  (taskId: string, linkId: string, payload: { label?: string; reference?: string }) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setTaskLoader(true));
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links/${linkId}`, payload);
      const { data } = response;
      if (data) {
        return data;
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'unable to add link please try again';
      captureException(error);
      dispatchTaskError(error);
      return undefined;
    } finally {
      dispatch(setTaskLoader(false));
    }
  };

/**
 * @desc taskDetails - add resource
 * @param {string} taskId - ID of the task
 * @param {string} linkId - ID of the link to be deleted
 */
export const deleteTaskLink: any = (taskId: string, linkId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links/${linkId}`);
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete link please try again';
    captureException(error);
    dispatchTaskError(error);
  } finally {
    dispatch(setTaskLoader(true));
  }
};

// Mobx - New services
export const getAllRawTasks = async (lastUpdatedDate?: Date) => {
  try {
    const lastUpdatedTime = lastUpdatedDate ? lastUpdatedDate.toISOString() : null;
    let allTasks: any[] = [];
    let currentPage = 0;

    while (true) {
      let url = `${REACT_APP_TEAMCAMP_APIURL}/v2/Tasks/getAllTasks?page=${currentPage}`;
      if (lastUpdatedTime) {
        url = url + `&lastUpdatedTime=${lastUpdatedTime}`;
      }

      const { data } = await axios.get(url);
      const { hasNextPage, tasks } = data as { hasNextPage: boolean; nextPage: string; tasks: any[] };

      allTasks = allTasks.concat(tasks);

      if (!hasNextPage) {
        break;
      }

      currentPage = currentPage + 1;
    }

    return allTasks.map(formatToRawTask);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    // showError(error); remove comment
    return [];
  }
};

/**
 * @function getAllProjectRawTasks - Fetches all tasks for specific projects and formats them into RawTask format
 * @param {string[]} projectIds - Array of project IDs
 */
export const getAllProjectRawTasks = async (projectIds: string[]) => {
  try {
    const allTasks: any[] = [];
    let currentPage = 0;

    while (true) {
      let url = `${REACT_APP_TEAMCAMP_APIURL}/v2/Tasks/getAllProjectTasks?page=${currentPage}`;

      const { data } = await axios.post(url, { projectIds });
      const { hasNextPage, tasks } = data as { hasNextPage: boolean; nextPage: string; tasks: any[] };

      allTasks.push(...tasks);

      if (!hasNextPage) {
        break;
      }

      currentPage = currentPage + 1;
    }

    return allTasks.map(formatToRawTask);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    // showError(error); remove comment
    return [];
  }
};

export const updateBulkStatus: any = (payload: any) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setTaskLoader(true));
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/putTasksStatus`, payload);
    return response;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add link please try again';
    captureException(error);
    dispatchTaskError(error);
    return undefined;
  } finally {
    dispatch(setTaskLoader(false));
  }
};

export function dispatchTaskError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchTaskSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
